/**
 * Piezas de interfaz compartidas.
 *
 * Pocas y propias, sin librería de componentes. Lo que tienen en común es la
 * identidad (tinta, papel, cobalto) y lo que no se negocia desde el día uno, la
 * accesibilidad (§10, AA): etiquetas asociadas a sus campos, errores anunciados
 * a los lectores de pantalla y estados de carga que no dejan adivinando.
 */

import { Eye, EyeOff } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------
 * Marca
 * ---------------------------------------------------------------------- */

/**
 * El símbolo: un punto —la nota— y tres titulares que salen de él —las
 * portadas—. Es el producto en un dibujo.
 *
 * `tone` existe porque el símbolo vive sobre dos fondos: sobre papel lleva su
 * propio cuadro de tinta; sobre la barra lateral, que ya es tinta, el cuadro
 * tiene que aclararse un punto o desaparece.
 */
export function LogoMark({ size = 32, tone = 'ink' }: { size?: number; tone?: 'ink' | 'on-ink' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 56 56" aria-hidden="true" className="shrink-0">
      <rect
        width="56"
        height="56"
        rx="14"
        fill={tone === 'ink' ? 'var(--color-ink)' : 'var(--color-ink-soft)'}
      />
      <circle cx="17" cy="28" r="6" fill="var(--color-brand)" />
      <rect x="28" y="15" width="17" height="5" rx="2.5" fill="var(--color-on-ink)" />
      <rect x="28" y="25.5" width="13" height="5" rx="2.5" fill="var(--color-on-ink)" />
      <rect x="28" y="36" width="17" height="5" rx="2.5" fill="var(--color-on-ink)" />
    </svg>
  );
}

export function Wordmark({ className = '' }: { className?: string }) {
  const { t } = useTranslation();

  return <span className={`font-display font-semibold tracking-tight ${className}`}>{t('app.name')}</span>;
}

/* -------------------------------------------------------------------------
 * Contenedores
 * ---------------------------------------------------------------------- */

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="w-full max-w-md rounded-2xl border border-[var(--color-line)] bg-[var(--color-card)] p-7 shadow-[0_1px_2px_rgb(20_33_61/0.04),0_8px_24px_rgb(20_33_61/0.06)]">
      {title ? <h1 className="mb-6 font-display text-2xl font-semibold tracking-tight">{title}</h1> : null}
      {children}
    </div>
  );
}

/** Tarjeta de contenido dentro del panel: sin ancho fijo ni título propio. */
export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-[var(--color-line)] bg-[var(--color-card)] ${className}`}>
      {children}
    </section>
  );
}

/**
 * Pantallas de acceso: la marca a un lado, el formulario al otro.
 *
 * En móvil el panel de marca desaparece y queda solo el logotipo encima del
 * formulario: en una pantalla estrecha, media pantalla de decoración antes del
 * campo de correo es justo lo que no hace falta.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden bg-[var(--color-ink)] p-10 text-[var(--color-on-ink)] lg:flex">
        <div className="flex items-center gap-3">
          <LogoMark size={36} tone="on-ink" />
          <Wordmark className="text-xl" />
        </div>

        <div>
          <p className="max-w-sm font-display text-4xl font-semibold leading-tight tracking-tight">
            {t('app.tagline')}
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[var(--color-on-ink-muted)]">
            {t('app.pitch')}
          </p>
        </div>

        {/* El mismo motivo del símbolo, a gran escala: una nota, varias portadas. */}
        <div aria-hidden="true" className="flex items-center gap-4 opacity-90">
          <span className="size-3 rounded-full bg-[var(--color-brand)]" />
          <div className="flex flex-1 flex-col gap-2">
            <span className="h-1.5 w-4/5 rounded-full bg-[var(--color-ink-line)]" />
            <span className="h-1.5 w-3/5 rounded-full bg-[var(--color-ink-line)]" />
            <span className="h-1.5 w-4/5 rounded-full bg-[var(--color-ink-line)]" />
          </div>
        </div>
      </aside>

      <main className="flex flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <LogoMark size={36} />
          <Wordmark className="text-xl" />
        </div>
        {children}
      </main>
    </div>
  );
}

export function CenteredLayout({ children }: { children: ReactNode }) {
  return <main className="flex min-h-screen items-center justify-center p-4">{children}</main>;
}

/* -------------------------------------------------------------------------
 * Formularios
 * ---------------------------------------------------------------------- */

export function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint ? (
        <p id={hintId} className="mt-1.5 text-xs leading-relaxed text-[var(--color-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const INPUT_CLASS =
  'w-full rounded-lg border border-[var(--color-line-strong)] bg-[var(--color-card)] px-3 py-2 text-sm outline-none transition placeholder:text-[var(--color-muted)]/70 hover:border-[var(--color-muted)] focus:border-[var(--color-brand)] focus:ring-3 focus:ring-[var(--color-brand-soft)]';

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={INPUT_CLASS} />;
}

/**
 * Campo de contraseña con el ojo para verla.
 *
 * Poder ver lo que se escribe no es una concesión a la comodidad: las
 * contraseñas largas, que son las que pedimos, se teclean mal a ciegas, y quien
 * falla tres veces acaba eligiendo una corta.
 *
 * Detalles que importan:
 *
 *   - Es un `button` de verdad con `aria-pressed`, no un icono con `onClick`:
 *     se alcanza con el teclado y un lector de pantalla dice su estado.
 *   - `type="button"` explícito. Dentro de un formulario, un botón sin tipo
 *     envía el formulario; el ojo iniciaría sesión al pulsarlo.
 *   - Cada montaje empieza oculto. Lo contrario dejaría una contraseña a la
 *     vista de quien pase por detrás solo por haberla mirado una vez.
 */
export function PasswordInput(props: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input {...props} type={visible ? 'text' : 'password'} className={`${INPUT_CLASS} pr-11`} />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-pressed={visible}
        aria-label={visible ? t('common.hidePassword') : t('common.showPassword')}
        title={visible ? t('common.hidePassword') : t('common.showPassword')}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-[var(--color-muted)] hover:text-[var(--color-ink)]"
      >
        {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      </button>
    </div>
  );
}

export function Button({
  children,
  loading,
  variant = 'primary',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  variant?: 'primary' | 'ghost';
}) {
  const base =
    'inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50';
  const styles =
    variant === 'primary'
      ? 'bg-[var(--color-brand)] text-white shadow-[0_1px_2px_rgb(43_78_255/0.35)] hover:bg-[var(--color-brand-strong)]'
      : 'border border-[var(--color-line-strong)] bg-[var(--color-card)] hover:bg-[var(--color-surface)]';

  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      // `aria-busy` es lo que hace que un lector de pantalla anuncie la espera;
      // sin esto, quien no ve el spinner no sabe que está pasando algo.
      aria-busy={loading}
      className={`${base} ${styles}`}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------
 * Avisos y estados
 * ---------------------------------------------------------------------- */

/**
 * Mensaje de error.
 *
 * `role="alert"` hace que los lectores de pantalla lo lean en cuanto aparece.
 * Un error que solo se ve es un error que la mitad de la gente se pierde.
 */
export function ErrorMessage({ children }: { children: ReactNode }) {
  if (!children) {
    return null;
  }

  return (
    <p
      role="alert"
      className="mb-4 rounded-lg border border-[var(--color-danger)]/20 bg-[var(--color-danger-soft)] px-3 py-2 text-sm text-[var(--color-danger)]"
    >
      {children}
    </p>
  );
}

/** Aviso de que algo salió bien, o simplemente informativo. */
export function Notice({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      className="mb-4 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success-soft)] px-3 py-2 text-sm text-[var(--color-success)]"
    >
      {children}
    </p>
  );
}

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
  info: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-strong)]',
  success: 'bg-[var(--color-success-soft)] text-[var(--color-success)]',
  warning: 'bg-[var(--color-warning-soft)] text-[var(--color-warning)]',
  danger: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
};

/**
 * Etiqueta de estado.
 *
 * El color nunca va solo: siempre lleva el texto del estado. Quien no distingue
 * el verde del rojo —uno de cada doce hombres— tiene que poder leer "Falló".
 */
export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}
